import { ApiError } from '../identity/types';
import { friendlyErrorMessage } from '../../shared/friendlyError';
import { HdcErrorCode, SERVICE_LINE_ERROR_CODES } from './types';

/** The stable problem `code` of an error, or null. Logic never matches on message text. */
export function errorCodeOf(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

export const isConcurrencyConflict = (err: unknown): boolean => err instanceof ApiError && err.status === 409 && err.code === HdcErrorCode.ConcurrencyConflict;
export const isForbidden = (err: unknown): boolean => err instanceof ApiError && err.status === 403;

const RAW_FALLBACK = /^Request to .+ failed with \d+$/;
const GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Plain-language copy for a failed call. A rule or validation refusal (400, 409, 422) carries a hand-authored, user-safe `detail`, which is
 * shown as received; a concurrency conflict and a 403 get their own wording; everything else (401, 429, 5xx with its operation reference id,
 * unknown) goes through the shared helper, so an unexpected server failure shows its reference id exactly as everywhere else.
 */
export const DEPENDENCY_UNAVAILABLE_MESSAGE =
  'EPN could not reach a connected service to check this, so nothing was saved. Please try again in a moment.';

export function healthcareErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && (err.code === HdcErrorCode.DependencyUnavailable || (err.status === 503 && !err.messageIsServerAuthored))) return DEPENDENCY_UNAVAILABLE_MESSAGE;
  if (isConcurrencyConflict(err)) return 'This was changed by someone else after you opened it, so your change was not saved. Reload, then make your change again.';
  if (err instanceof ApiError && (err.status === 400 || err.status === 409 || err.status === 422) && err.messageIsServerAuthored && err.message && !RAW_FALLBACK.test(err.message)) {
    return err.message;
  }
  if (isForbidden(err)) return 'You do not have permission to do that. Ask a company administrator if you need access.';
  return friendlyErrorMessage(err, fallback);
}

function detailString(err: ApiError, key: string): string | null {
  const value = err.details?.[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The id of the row a 409 RATE_OVERLAP names: the problem's own member when it has one, else the first id in the server's sentence. */
export function conflictingRateId(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== HdcErrorCode.RateOverlap) return null;
  const named = detailString(err, 'conflictingRateId') ?? detailString(err, 'conflictingRowId') ?? detailString(err, 'conflictingId');
  if (named) return named;
  const row = /\brow ([\w-]+)/i.exec(err.message)?.[1];
  return row ?? err.message.match(GUID)?.[0] ?? null;
}

/** The ids a 422 RATE_AMBIGUOUS names. */
export function ambiguousRateIds(err: unknown): string[] {
  if (!(err instanceof ApiError) || err.code !== HdcErrorCode.RateAmbiguous) return [];
  const listed = err.details?.rateIds;
  if (Array.isArray(listed)) return listed.filter((x): x is string => typeof x === 'string');
  return err.message.match(GUID) ?? [];
}

export interface ServiceLineProblem {
  code: string;
  title: string;
  /** The server's own sentence. */
  detail: string;
  /** What to do about it, in plain language. */
  fix: string;
}

const PROBLEMS: Record<string, { title: string; fix: string }> = {
  [HdcErrorCode.RateNotFound]: {
    title: 'No rate is set for this work',
    fix: 'Add a company rate or an employee rate for this worker category, work type and pay basis (Settings, Payroll, Compensation Rates; or the employee’s Worker category and rates), then calculate again. Nothing is guessed and zero is never used.',
  },
  [HdcErrorCode.RateAmbiguous]: {
    title: 'More than one equally specific rate applies',
    fix: 'Open Compensation Rates and end or correct one of the overlapping rates so only one applies on the service date, then calculate again. The line was not priced and no rate was picked for you.',
  },
  [HdcErrorCode.DisciplineNotAssigned]: {
    title: 'This employee does not hold that worker category',
    fix: 'Open the employee’s profile, Payroll Details, Worker category and rates, and add the worker category (effective on the service date), or choose a worker category the employee already has.',
  },
  [HdcErrorCode.WorkTypeNotAllowed]: {
    title: 'This employee is not set up for that work type',
    fix: 'Open the employee’s profile, Payroll Details, Work types this employee may be paid for, and add the work type, or choose one on the list. A line for an employee with a list must name one of those work types.',
  },
  [HdcErrorCode.DoublePaymentBlocked]: {
    title: 'Two lines would pay the same work twice',
    fix: 'Remove one of the lines. If both really are owed, mark both as an additional earning and give a reason; that choice is recorded for the approver.',
  },
  [HdcErrorCode.RateNotClientSupplied]: {
    title: 'A rate cannot be typed on this kind of line',
    fix: 'Remove the rate, flat amount and hours from the line. EPN picks the rate from your rate schedule when the run is calculated.',
  },
  [HdcErrorCode.ServiceLineInvalid]: {
    title: 'A service line is incomplete or out of range',
    fix: 'Check the worker category, the quantity (more than 0 and up to 10,000), the worked hours (0 to 744) and the service date (inside the pay period).',
  },
};

/**
 * A Module 6 coded rejection reaches a Custom Pay Run as an employee's exception text: "RATE_NOT_FOUND: Earning line 1 (...): ...". The same plain-language title and fix
 * are shown for it. Null when the text carries none of the service line codes.
 */
export function describeExceptionProblem(text: string | null | undefined): ServiceLineProblem | null {
  const match = /^([A-Z][A-Z_]+):\s*([\s\S]*)$/.exec((text ?? '').trim());
  if (!match || !SERVICE_LINE_ERROR_CODES.includes(match[1]!)) return null;
  const known = PROBLEMS[match[1]!]!;
  return { code: match[1]!, title: known.title, detail: match[2]!.trim(), fix: known.fix };
}

/** The structured, plain-language explanation of a refused service line, or null when the error is something else. */
export function describeServiceLineProblem(err: unknown): ServiceLineProblem | null {
  if (!(err instanceof ApiError) || !err.code || !SERVICE_LINE_ERROR_CODES.includes(err.code)) return null;
  const known = PROBLEMS[err.code]!;
  const detail = err.messageIsServerAuthored && err.message && !RAW_FALLBACK.test(err.message) ? err.message : '';
  return { code: err.code, title: known.title, detail, fix: known.fix };
}
